import type { ConflictResult, ConflictRule } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { isoWeekdayFromDateKey } from '@zag/calendar/models';
import { isWorkdayInMask } from '../../utils/employment-capacity.utils';

/**
 * Detects conflicts when persons are scheduled on non-working days.
 *
 * Unlike HolidayConflictRule, this rule does not use CalendarEvent[].
 * Instead, it receives the strategy's workingDays (ISO weekday numbers)
 * and checks each date in the schedule against the weekday pattern.
 *
 * It also honours per-person working-days masks: a person scheduled on a team
 * working day that is not one of THEIR working days (per their employment
 * workday mask) is flagged too. Persons without a mask entry fall back to the
 * team-level pattern only.
 *
 * Produces a ConflictResult per person scheduled on a non-working day.
 */
export class ClosedDayConflictRule implements ConflictRule {
  readonly type = 'closed_day' as const;

  constructor(
    private readonly workingDays: number[],
    private readonly personWorkdayMasks: ReadonlyMap<string, number> = new Map()
  ) {}

  detect(schedule: ScheduleMap): ConflictResult[] {
    const results: ConflictResult[] = [];
    const workingDaySet = new Set(this.workingDays);

    for (const [dateStr, daySchedule] of Object.entries(schedule)) {
      // Derive ISO weekday (0=Mon…6=Sun) directly from the date string — no Date needed
      const isoWeekday = isoWeekdayFromDateKey(dateStr) ?? -1;
      const isTeamWorkingDay = workingDaySet.has(isoWeekday);

      for (const personId of Object.keys(daySchedule)) {
        // A day is closed for a person when it is a team non-working day, OR the
        // person has an explicit workday mask and this weekday is not in it.
        const mask = this.personWorkdayMasks.get(personId);
        const isPersonNonWorkingDay = mask !== undefined && !isWorkdayInMask(mask, isoWeekday);
        if (isTeamWorkingDay && !isPersonNonWorkingDay) continue;

        results.push({
          type: 'closed_day',
          date: dateStr,
          personId,
          eventLabel: 'Closed day',
          calendarId: 'strategy',
        });
      }
    }

    return results;
  }
}
